import { ExtendedMonitoredTv, TVTimePeriodSelection } from '../../model/SOL49';
import TimeUtils from './TimeUtils';
import AppContext from '../context/AppContext';
import MonitoredLoadUtils from './MonitoredLoadUtils';
import TimePeriodUtils from './TimePeriodUtils';
import { MonitoredLoadValue, TimePeriod } from '../../model/CORE';
import Utils from './Utils';

export class DCBSolverUtils {
    /**
     * Returns true if at least one of the provided monitored TVs has an active period after provided time
     *
     * @param afterTime
     * @param monitoredTvs
     */
    static hasActivePeriodAfter(afterTime: Date, activePeriods: Array<TimePeriod>): boolean {
        if (!afterTime || !activePeriods?.length) return false;

        for (const activePeriod of activePeriods) {
            if (TimeUtils.diffInMs(activePeriod.to, afterTime) > 0) {
                return true;
            }
        }
        return false;
    }

    /**
     * Looks for the first highest load value after a given time, that can be found for a given list of monitored TVs,
     * and return the corresponding periods.
     *
     * The load values that are being considered are the ones within the activation periods.
     *
     * Values can be predefined by the optional presets parameter
     *
     * @param monitoredTvs
     * @param afterTime
     * @param presets
     */
    static getAutoTvSelection(
        monitoredTvs: Array<ExtendedMonitoredTv>,
        afterTime: Date,
        presets?: Array<TVTimePeriodSelection>,
    ): Array<TVTimePeriodSelection> {
        return monitoredTvs?.map(monitoredTv => {
            // Check if selection/period is being forced
            const preset = presets?.find(tvTime => tvTime.tvId === monitoredTv.refLocation.id);
            if (Utils.isExists(preset) && preset.timePeriod) return preset;

            // Reduce and adapt the list active periods so that they are after time
            const activePeriodsAfterTime = monitoredTv.activePeriods
                .filter(activePeriod => TimeUtils.diffInMs(activePeriod.to, afterTime) > 0)
                .map(activePeriod => {
                    return {
                        from: TimeUtils.max(afterTime, activePeriod.from),
                        to: activePeriod.to,
                    };
                });

            // Build a list of load values that correspond to load values during the active periods
            const activeLoadValues = [];
            activePeriodsAfterTime.forEach(activePeriod => {
                activeLoadValues.push(
                    ...monitoredTv.loadValues
                        .filter(loadValue => TimePeriodUtils.intersects(activePeriod, loadValue.timePeriod))
                        .map(loadValue => {
                            return {
                                timePeriod: TimePeriodUtils.intersection(activePeriod, loadValue.timePeriod),
                                loadClassification: loadValue.loadClassification,
                                nonRelevant: loadValue.nonRelevant,
                            } as MonitoredLoadValue;
                        }),
                );
                //TODO add occ load values because entry could be empty
            });

            // Look for the first highest load value
            const futureLoadValues = activeLoadValues.filter(
                mlv => TimeUtils.diffInMs(mlv.timePeriod.to, AppContext.getCurrentTime()) > 0,
            );
            const highestLC = MonitoredLoadUtils.getHighestLoadClassification(futureLoadValues);
            const highestMLV = highestLC && futureLoadValues.find(mlv => mlv.loadClassification === highestLC);

            // Return it
            return {
                tvId: monitoredTv.refLocation.id,
                selected: Utils.isExists(highestMLV),
                timePeriod: highestMLV ? Object.assign({}, highestMLV.timePeriod) : null,
            } as TVTimePeriodSelection;
        });
    }
}
